import * as z from 'zod';

// 郵便番号検索APIが受け付けるのはハイフンなし7桁のみ
const searchableZipCodeRegex = /^\d{7}$/;

// 入力値を検索・保存に使う形（ハイフンなし）へ揃える
export const normalizeZipCode = (zipCode: string): string =>
  zipCode.trim().replaceAll('-', '');

// 郵便番号検索APIを呼べる形か
export const isSearchableZipCode = (zipCode: string): boolean =>
  searchableZipCodeRegex.test(normalizeZipCode(zipCode));

export const ZIP_CODE_FIRST_LENGTH = 3;
export const ZIP_CODE_LAST_LENGTH = 4;
const ZIP_CODE_LENGTH = ZIP_CODE_FIRST_LENGTH + ZIP_CODE_LAST_LENGTH;

export type ZipCodeParts = {first: string; last: string};

// ハイフンや空白が混じったコピペ・住所自動入力の値から数字だけを取り出す
const toZipCodeDigits = (value: string): string => value.replaceAll(/\D/g, '');

const toZipCodeParts = (digits: string): ZipCodeParts => ({
  first: digits.slice(0, ZIP_CODE_FIRST_LENGTH),
  last: digits.slice(ZIP_CODE_FIRST_LENGTH, ZIP_CODE_LENGTH),
});

// 上3桁と下4桁の入力欄へ振り分ける。ハイフン区切りの値は区切り位置をそのまま使い、
// 下4桁だけが入力された状態を上3桁へずらさない
export const splitZipCode = (zipCode: string): ZipCodeParts => {
  const [first, last] = zipCode.split('-');

  if (first === undefined || last === undefined) {
    return toZipCodeParts(toZipCodeDigits(zipCode));
  }

  return {
    first: toZipCodeDigits(first).slice(0, ZIP_CODE_FIRST_LENGTH),
    last: toZipCodeDigits(last).slice(0, ZIP_CODE_LAST_LENGTH),
  };
};

// 2つの入力欄の値を保存・検索に使う1本の郵便番号へ戻す
export const joinZipCode = ({first, last}: ZipCodeParts): string => {
  const firstDigits = toZipCodeDigits(first);
  const lastDigits = toZipCodeDigits(last);

  // 片方の欄に7桁が入るのは住所自動入力かコピペだけなので、その入力を
  // 上3桁と下4桁へ振り分け直す。手入力の桁あふれと区別するため7桁を条件にする
  const pasted = [firstDigits, lastDigits].find(
    (digits) => digits.length >= ZIP_CODE_LENGTH,
  );

  if (pasted !== undefined) {
    const parts = toZipCodeParts(pasted);
    return `${parts.first}-${parts.last}`;
  }

  // 桁あふれは超過分を捨てる。もう片方の欄に入っている値は壊さない
  const firstValue = firstDigits.slice(0, ZIP_CODE_FIRST_LENGTH);
  const lastValue = lastDigits.slice(0, ZIP_CODE_LAST_LENGTH);

  if (firstValue === '' && lastValue === '') {
    return '';
  }

  // 入力途中でどちらか一方だけが埋まっている状態を保つためハイフンで繋ぐ
  return `${firstValue}-${lastValue}`;
};

// 郵便番号検索APIのレスポンス。町域は市区町村に連結された形で address1 に入る
export const ZipCodeSearchResponse = z.object({
  zipCode: z.string(),
  prefectureID: z.number(),
  address1: z.string(),
});

export type ZipCodeSearchResponseType = z.infer<typeof ZipCodeSearchResponse>;
